<template>
  <div class="">
    <breadcrumb-component
      :moduleTitle="'Support'"
      :mainTitle="'My Inquiry'"
      :subTitle="'Inquiry list'"
    />
    <div class="row">
      <div class="col-md-12">
        <div class="card card_full_window">
          <div class="card-body">
            <div
              v-if="can('my_inquiry')"
              class="common_content_wrap no_left_content"
            >
              <div class="d_flex_btwn border-lightgray-bottom pb_15 mb_0">
                <h5 class="common_subtitle">My Inquiry</h5>
                <button
                  v-if="can('create_ticket')"
                  type="button"
                  class="btn btn-outline-primary btn-sm width_100p"
                  @click="openModal"
                >
                  New Ticket
                </button>
              </div>

              <cloud-table-component
                :columns="columns"
                :rows="inquiries"
                :sortable="sortable"
                :pagination="pagination"
                @sorting="sorting"
                @pageChanged="pageChanged"
              >
                <template v-slot:status="{ row }">
                  <span v-if="row.status === 'processing'" class="color_warning"
                    >Processing</span
                  >
                  <span v-if="row.status === 'running'" class="color_success"
                    >Running</span
                  >
                  <span v-if="row.status === 'stopped'" class="color_danger"
                    >Stopped</span
                  >
                </template>

                <template v-slot:action="{ row }">
                  <button
                    v-if="can('view_ticket')"
                    type="button"
                    class="btn btn-outline-primary btn-sm mr_15 width_100p"
                    @click.prevent="openDetailsInquiry(row.id)"
                  >
                    Details
                  </button>
                </template>
              </cloud-table-component>
            </div>
            <div v-else>
              <p>You do not have permission to view this content.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BaseModal
      v-if="isShowInquiryModal"
      :title="title"
      :closeModal="closeModal"
      size="modal_850p"
    >
      <InquiryForm
        :closeModal="closeModal"
        :loadData="getMyInquiry"
        :editId="editId"
      />
    </BaseModal>

    <BaseModal
      v-if="isShowInquiryDetailsModal"
      title="Inquiry Details"
      :closeModal="closeDetailsInquiry"
      size="modal_850p"
    >
      <InquiryDetails
        :closeModal="closeDetailsInquiry"
        :loadData="getMyInquiry"
        :inquiryId="inquiryId"
      />
    </BaseModal>
  </div>
</template>

<script>
import CloudTableComponent from '@/components/shared/CloudTableComponent.vue';
import InquiryForm from '@/views/account/InquiryForm.vue';
import InquiryDetails from '@/views/inquiry/InquiryDetails.vue';
import BaseModal from '@/components/BaseModal.vue';
import { useAbility } from '@casl/vue';
import BreadcrumbComponent from '@/components/shared/BreadcrumbComponent.vue';

export default {
  name: 'MyInquiry',
  components: {
    BreadcrumbComponent,
    BaseModal,
    InquiryForm,
    CloudTableComponent,
    InquiryDetails,
  },
  setup() {
    const { can } = useAbility();

    return {
      can,
    };
  },
  data() {
    return {
      inquiries: [],
      filter: {
        server_region_id: '',
      },
      queryParams: {
        page: 1,
        search: '',
        search_columns:
          'ticket_number,cloudService.name,cloudServer.server_name,subject,status',
        sort: 'created_at',
        sort_order: 'desc',
        per_page: 10,
      },
      sortable: {
        order: 'created_at',
        sort: 'desc',
      },
      columns: [
        {
          label: 'Ticket Number',
          field: 'ticket_number',
          sortable: true,
        },
        {
          label: 'Service Provider',
          field: 'additional_data.service_name',
        },
        {
          label: 'Server',
          field: 'additional_data.server_name',
        },
        {
          label: 'Title',
          field: 'subject',
          sortable: true,
        },
        {
          label: 'Status',
          field: 'status',
          sortable: true,
        },
        {
          label: 'Submission Date',
          field: 'created_at',
          sortable: true,
        },
        {
          label: 'Re-Inquiry',
          field: 'action',
          sortable: false,
          slot: true,
        },
      ],
      pagination: null,
      isShowInquiryModal: false,
      editId: null,
      isShowInquiryDetailsModal: false,
      inquiryId: null,
    };
  },
  watch: {
    $route(to, from) {
      if (to.name === from.name && !window._.isEqual(to, from)) {
        this.setQueryData(this.getAllQueryParams());
        this.getMyInquiry();
      }
    },
  },
  computed: {
    title() {
      if (this.editId) {
        return 'Add New Ticket';
      } else {
        return 'Edit Ticket';
      }
    },
  },
  mounted() {
    const query = this.getAllQueryParams();
    if (query.page) {
      this.setQueryData(query);
    }
    this.getMyInquiry();
  },
  methods: {
    setQueryData(query) {
      this.queryParams = {
        page: query.page,
        search: query.search,
        search_columns: query.search_columns,
        sort: query.sort,
        sort_order: query.sort_order,
        per_page: query.per_page,
      };

      this.filter = {
        server_region_id: query.server_region_id,
      };

      this.sortable = {
        order: query.sort,
        sort: query.sort_order,
      };
    },
    resetAll() {
      this.sortable = {
        order: 'created_at',
        sort: 'desc',
      };

      this.filter = {
        server_region_id: '',
      };

      this.queryParams = {
        page: 1,
        search: '',
        search_columns:
          'ticket_number,cloudService.name,cloudServer.server_name,subject,status',
        sort: 'created_at',
        sort_order: 'desc',
        per_page: 10,
      };

      this.changeRoute();
      this.loadData();
    },
    searchQuery() {
      this.changeRoute();
    },
    sorting({ column, order }) {
      this.queryParams.sort = column;
      this.queryParams.sort_order = order;
      this.queryParams.page = 1;
      this.changeRoute();
    },
    pageChanged(page) {
      this.queryParams.page = page;
      this.changeRoute();
    },
    changeRoute() {
      this.$router.push({
        name: this.$route.name,
        query: { ...this.queryParams, ...this.filter },
      });
    },
    getMyInquiry() {
      this.lyLoading = true;
      let params = { ...this.queryParams, ...this.filter };
      this.axios
        .get('/my-inquiry', {
          params: params,
        })
        .then(({ data }) => {
          this.inquiries = data?.data?.data;
          this.pagination = data?.data?.meta;
        })
        .finally(() => {
          this.lyLoading = false;
        });
    },
    openModal(editId = null) {
      this.editId = editId;
      this.isShowInquiryModal = true;
    },
    closeModal() {
      this.isShowInquiryModal = false;
    },
    openDetailsInquiry(inquiryId) {
      this.inquiryId = inquiryId;
      this.isShowInquiryDetailsModal = true;
    },
    closeDetailsInquiry() {
      this.inquiryId = null;
      this.isShowInquiryDetailsModal = false;
    },
  },
};
</script>

<style scoped></style>
